import React, { useState, useEffect } from 'react';
import LayoutShell from '../components/LayoutShell.jsx';
import ViewToggle from '../components/ViewToggle.jsx';
import FiltersPane from '../components/FiltersPane.jsx';
import RiskDetailDrawer from '../components/RiskDetailDrawer.jsx';
import RiskRegisterTable from '../components/RiskRegisterTable.jsx';
import KanbanBoard from '../components/KanbanBoard.jsx';
import { RiskProvider } from '../context/RiskContext.jsx';
import RiskImpactMatrix from '../risk_dashboard.jsx';
import ListManagerModal from '../components/ListManagerModal.jsx';
import { useRiskContext } from '../context/RiskContext.jsx';
import { HelpCircle, Settings } from 'lucide-react';
import HelpModal from '../components/HelpModal.jsx';
import ColumnManagerModal from '../components/ColumnManagerModal.jsx';
import * as XLSX from 'xlsx';

export default function RisksPage() {
  const [manageType, setManageType] = useState(null); // 'category' | 'status' | 'action'

  return (
    <RiskProvider>
      <RisksInner setManageType={setManageType} />
      <ManageModalWrapper manageType={manageType} setManageType={setManageType} />
    </RiskProvider>
  );
}

function RisksInner({ setManageType }) {
  const ctx = useRiskContext();
  const { handleAddRiskModal, exportToSpreadsheet, exportToPDF, importFromSpreadsheet, downloadTemplate } = ctx;
  const [viewMode, setViewMode] = useState('table');
  const [isFullView, setIsFullView] = useState(false);
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [helpOpen, setHelpOpen] = useState(false);
  const [showSheetMenu, setShowSheetMenu] = useState(false);
  // Column customization
  const defaultColumns = [
    { key: 'id', label: 'ID', editable: false },
    { key: 'description', label: 'Description', editable: false },
    { key: 'initialLikelihood', label: 'Initial L', editable: true, type: 'select' },
    { key: 'initialImpact', label: 'Initial I', editable: true, type: 'select' },
    { key: 'initialScore', label: 'Initial Score', editable: false, badge: 'initial' },
    { key: 'category', label: 'Category', editable: true, type: 'select' },
    { key: 'action', label: 'Action', editable: true, type: 'select' },
    { key: 'actionDescription', label: 'Action Description', editable: true, type: 'textarea' },
    { key: 'actionOwner', label: 'Action Owner', editable: true },
    { key: 'deadline', label: 'Deadline', editable: true, type: 'date' },
    { key: 'status', label: 'Status', editable: true, type: 'select' },
    { key: 'likelihood', label: 'Residual Likelihood', editable: true, type: 'select' },
    { key: 'impact', label: 'Residual Impact', editable: true, type: 'select' },
    { key: 'riskScore', label: 'Residual Score', editable: false, badge: 'residual' }
  ];

  const [columns, setColumns] = useState(() => {
    const stored = localStorage.getItem('rrColumns');
    return stored ? JSON.parse(stored) : defaultColumns;
  });

  useEffect(() => {
    localStorage.setItem('rrColumns', JSON.stringify(columns));
  }, [columns]);

  const [manageColumnsOpen, setManageColumnsOpen] = useState(false);

  const toggleFullView = () => {
    setIsFullView(!isFullView);
  };

  const toggleSidebar = () => {
    setSidebarVisible(!sidebarVisible);
  };

  // Show sidebar when visible AND not in full view
  const sidebarContent = (sidebarVisible && !isFullView) ? (
    <FiltersPane 
      onManageCategories={() => setManageType('category')} 
      onManageStatus={() => setManageType('status')} 
      onManageActions={() => setManageType('action')}
      onHideSidebar={toggleSidebar}
    />
  ) : null;

  // Dynamic Import / Export based on current columns
  const exportSpreadsheetDynamic = () => {
    const visibleCols = columns.filter(c=>!c.hidden);
    const header = visibleCols.map(c=>c.label);
    const rows = ctx.risks.map(risk => visibleCols.map(col=>risk[col.key] ?? ''));
    const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Register');
    XLSX.writeFile(wb, 'risk-register.xlsx');
  };

  const downloadTemplateDynamic = () => {
    const header = columns.filter(c=>!c.hidden).map(c=>c.label);
    const ws = XLSX.utils.aoa_to_sheet([header]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, 'risk-register-template.xlsx');
  };

  const importSpreadsheetDynamic = (file) => {
    if(!file) return;
    const reader = new FileReader();
    reader.onload = (e)=>{
      const data = new Uint8Array(e.target.result);
      const wb = XLSX.read(data,{type:'array'});
      const ws = wb.Sheets[wb.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(ws,{defval:'',header:1});
      if(json.length<2) return; // only headers
      const header = json[0];
      const rows = json.slice(1);
      const colMap = {};
      header.forEach((h,i)=>{
        const colDef = columns.find(c=>c.label===h);
        if(colDef) colMap[i]=colDef.key;
      });
      const imported = rows.map((row,idx)=>{
        const obj={id: ctx.risks.length+idx+1};
        Object.entries(colMap).forEach(([i,key])=>{ obj[key]=row[i] });
        return obj;
      });
      ctx.setRisks(prev=>[...prev,...imported]);
    };
    reader.readAsArrayBuffer(file);
  };

  return (
    <LayoutShell sidebar={sidebarContent}> 
      {/* Fixed Filter Toggle Button - Top Left Corner */}
      {!sidebarVisible && (
        <button
          onClick={toggleSidebar}
          className="fixed top-4 left-4 z-50 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow-lg transition-colors duration-200 flex items-center gap-2"
          title="Show Filters"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          Filters
        </button>
      )}

      <div className="p-6 space-y-6">
        {/* Dashboard Section - Hidden in full view to maximize register space */}
        {!isFullView && (
          <div className="space-y-4">
            <RiskImpactMatrix viewMode="both" hideFilters hideKanban hideRegister />
          </div>
        )}

        {/* Register Section */}
        <div className={`${!isFullView ? 'mt-8' : ''} space-y-3`}>
          {/* Header with Sidebar Toggle */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-bold text-gray-900">Risk Register</h1>
            </div>

            <div className="flex items-center gap-4">
              <ViewToggle 
                value={viewMode} 
                onChange={setViewMode}
                options={[
                  { value: 'table', label: 'Table' },
                  { value: 'kanban', label: 'Kanban' }
                ]}
              />
              <button
                onClick={toggleFullView}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-colors duration-200"
                title={isFullView ? "Exit Full View" : "Enter Full View"}
              >
                {isFullView ? "Exit Full View" : "Full View"}
              </button>
              <button onClick={() => setManageColumnsOpen(true)} title="Customize Columns" className="text-gray-600 hover:text-gray-800">
                <Settings className="w-5 h-5" />
              </button>
              <button onClick={() => setHelpOpen(true)} title="Help" className="text-gray-600 hover:text-gray-800">
                <HelpCircle className="w-6 h-6" />
              </button>
            </div>
          </div>
          
          <div className="flex items-center justify-between">
            {/* Buttons on right */}
            <div className="flex gap-2 items-center">
              <button onClick={handleAddRiskModal} className="bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700 text-sm">Add Risk</button>
              <input type="file" accept=".xlsx,.xls" id="import-file" className="hidden" onChange={(e)=>{importSpreadsheetDynamic(e.target.files[0]); e.target.value='';}} />

              {/* Spreadsheet dropdown */}
              <div className="relative" onBlur={(e)=>{ if(!e.currentTarget.contains(e.relatedTarget)) setShowSheetMenu(false); }}>
                <button
                  type="button"
                  onClick={()=>setShowSheetMenu(prev=>!prev)}
                  className="bg-gray-700 text-white px-3 py-2 rounded hover:bg-gray-800 text-sm flex items-center gap-1"
                >
                  Import / Export
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                </button>

                {showSheetMenu && (
                  <div className="absolute right-0 mt-1 w-48 bg-white border rounded shadow-lg z-10 text-sm">
                    <button
                      className="w-full text-left px-4 py-2 hover:bg-gray-100"
                      onClick={()=>{document.getElementById('import-file').click(); setShowSheetMenu(false);}}
                    >Import…</button>
                    <button
                      className="w-full text-left px-4 py-2 hover:bg-gray-100"
                      onClick={()=>{downloadTemplateDynamic(); setShowSheetMenu(false);}}
                    >Download Template</button>
                    <button
                      className="w-full text-left px-4 py-2 hover:bg-gray-100"
                      onClick={()=>{exportSpreadsheetDynamic(); setShowSheetMenu(false);}}
                    >Export Spreadsheet</button>
                    <button
                      className="w-full text-left px-4 py-2 hover:bg-gray-100"
                      onClick={()=>{exportToPDF(); setShowSheetMenu(false);}}
                    >Export PDF</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {viewMode === 'table' && <RiskRegisterTable columns={columns.filter(c=>!c.hidden)} setColumns={setColumns} />}
        {viewMode === 'kanban' && <KanbanBoard />}
      </div>
      <RiskDetailDrawer />
      <HelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
      <ColumnManagerModal open={manageColumnsOpen} onClose={()=>setManageColumnsOpen(false)} columns={columns} setColumns={setColumns} defaultColumns={defaultColumns} />
    </LayoutShell>
  );
}

function ManageModalWrapper({ manageType, setManageType }) {
  const ctx = useRiskContext();
  if (!manageType) return null;

  let props = {};
  if (manageType === 'category') {
    props = {
      title: 'Categories',
      items: ctx.categories,
      addItem: ctx.addCategory,
      updateItem: ctx.updateCategory,
      deleteItem: ctx.deleteCategory
    };
  } else if (manageType === 'status') {
    props = {
      title: 'Statuses',
      items: ctx.statusOptions,
      addItem: ctx.addStatus,
      updateItem: ctx.updateStatus,
      deleteItem: ctx.deleteStatus
    };
  } else if (manageType === 'action') {
    props = {
      title: 'Actions',
      items: ctx.riskActions,
      addItem: ctx.addAction,
      updateItem: ctx.updateAction,
      deleteItem: ctx.deleteAction
    };
  }
  return <ListManagerModal open={true} onClose={()=>setManageType(null)} {...props} />;
} 